import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarClock, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import TransactionDialog from "../components/TransactionDialog";
import ProjectionCard from "../components/ProjectionCard";
import api, { fmtNum, fmtDate } from "../lib/api";

const HR_MONTHS = ["Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj", "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac"];
const HR_DAYS = ["Pon", "Uto", "Sri", "Čet", "Pet", "Sub", "Ned"];

const short = (n) => (Math.abs(n) >= 10000 ? `${(n / 1000).toLocaleString("hr-HR", { maximumFractionDigits: 1 })}k` : fmtNum(n));

export default function CalendarPage() {
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [txs, setTxs] = useState([]);
  const [dayOpen, setDayOpen] = useState(null);
  const [planDate, setPlanDate] = useState(null);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/transactions", { params: { month: ym, limit: 1000 } }).then((r) => setTxs(r.data));
  useEffect(() => { load(); }, [ym]);

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const [year, month] = ym.split("-").map(Number);
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const shiftMonth = (d) => {
    const dt = new Date(year, month - 1 + d, 1);
    setYm(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  };

  const byDay = useMemo(() => {
    const map = {};
    txs.forEach((t) => {
      (map[t.date] = map[t.date] || []).push(t);
    });
    return map;
  }, [txs]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const dayStats = (dateStr) => {
    const list = byDay[dateStr] || [];
    let rin = 0, rout = 0, pin = 0, pout = 0, overdue = false;
    list.forEach((t) => {
      const planned = t.status === "planirano";
      if (t.type === "naplata") planned ? (pin += t.amount_eur) : (rin += t.amount_eur);
      else planned ? (pout += t.amount_eur) : (rout += t.amount_eur);
      if (planned && dateStr < today) overdue = true;
    });
    return { list, rin, rout, pin, pout, overdue };
  };

  const monthTotals = useMemo(() => {
    let rin = 0, rout = 0, pin = 0, pout = 0;
    txs.forEach((t) => {
      const planned = t.status === "planirano";
      if (t.type === "naplata") planned ? (pin += t.amount_eur) : (rin += t.amount_eur);
      else planned ? (pout += t.amount_eur) : (rout += t.amount_eur);
    });
    return { rin, rout, pin, pout };
  }, [txs]);

  const openList = dayOpen ? byDay[dayOpen] || [] : [];

  return (
    <div className="space-y-6 fade-up" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Dospijeća kroz mjesec</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Kalendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev-month" onClick={() => shiftMonth(-1)} className="w-9 h-9 flex items-center justify-center rounded-sm bg-white border border-[#CBD5E1] hover:bg-[#F1F5F9] transition-colors duration-200">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <p data-testid="calendar-month-label" className="font-heading text-lg font-semibold text-[#0F172A] w-44 text-center">{HR_MONTHS[month - 1]} {year}.</p>
          <button data-testid="calendar-next-month" onClick={() => shiftMonth(1)} className="w-9 h-9 flex items-center justify-center rounded-sm bg-white border border-[#CBD5E1] hover:bg-[#F1F5F9] transition-colors duration-200">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#475569] bg-white border border-[#E2E8F0] rounded-sm px-4 py-3" data-testid="calendar-legend">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#0D9488]" /> Priljev (ostvareno): <b className="tabular-nums text-[#0D9488]">+{fmtNum(monthTotals.rin, "€")}</b></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#DC2626]" /> Odljev (ostvareno): <b className="tabular-nums text-[#DC2626]">−{fmtNum(monthTotals.rout, "€")}</b></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-[#D97706]" /> Planirano: <b className="tabular-nums text-[#D97706]">+{fmtNum(monthTotals.pin)} / −{fmtNum(monthTotals.pout)} €</b></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#DC2626] animate-pulse" /> Dospjelo neupareno</span>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F1F5F9]">
          {HR_DAYS.map((d) => (
            <div key={d} className="px-2 py-2 text-[11px] uppercase tracking-wider font-semibold text-[#475569] text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="calendar-grid">
          {cells.map((day, i) => {
            if (!day) return <div key={`b${i}`} className="min-h-[92px] border-b border-r border-[#F1F5F9] bg-[#F8FAFC]/60" />;
            const dateStr = `${ym}-${String(day).padStart(2, "0")}`;
            const s = dayStats(dateStr);
            const isToday = dateStr === today;
            const weekend = (firstWeekday + day - 1) % 7 >= 5;
            const hasData = s.list.length > 0;
            return (
              <button
                key={dateStr}
                data-testid={`calendar-day-${dateStr}`}
                onClick={() => (hasData ? setDayOpen(dateStr) : setPlanDate(dateStr))}
                title={hasData ? "" : "Klikni za novi plan"}
                className={`group min-h-[56px] sm:min-h-[92px] border-b border-r border-[#F1F5F9] p-1.5 text-left align-top flex flex-col gap-1 transition-colors duration-200 ${weekend ? "bg-[#F8FAFC]/80" : "bg-white"} cursor-pointer hover:bg-[#0D9488]/5 ${isToday ? "ring-2 ring-inset ring-[#0D9488]" : ""}`}
              >
                <span className="flex items-center justify-between">
                  <span className={`text-xs font-semibold ${isToday ? "text-white bg-[#0D9488] rounded-full w-5 h-5 flex items-center justify-center" : "text-[#475569]"}`}>
                    {day}
                  </span>
                  {!hasData && <Plus className="w-3.5 h-3.5 text-[#0D9488] opacity-25 group-hover:opacity-100 transition-opacity duration-200" />}
                </span>
                {s.rin > 0 && <span className="hidden sm:block text-xs leading-tight tabular-nums font-semibold text-[#0D9488] bg-[#0D9488]/10 rounded-sm px-1 py-0.5 truncate">+{short(s.rin)}</span>}
                {s.rout > 0 && <span className="hidden sm:block text-xs leading-tight tabular-nums font-semibold text-[#DC2626] bg-[#DC2626]/10 rounded-sm px-1 py-0.5 truncate">−{short(s.rout)}</span>}
                {(s.pin > 0 || s.pout > 0) && (
                  <span className={`hidden sm:block text-xs leading-tight tabular-nums font-medium border border-dashed rounded-sm px-1 py-0.5 truncate ${s.overdue ? "text-[#DC2626] border-[#DC2626] bg-[#DC2626]/5" : "text-[#D97706] border-[#D97706]/60"}`}>
                    {s.overdue ? "⚠ " : "◌ "}{s.pin > 0 ? `+${short(s.pin)}` : ""}{s.pin > 0 && s.pout > 0 ? " / " : ""}{s.pout > 0 ? `−${short(s.pout)}` : ""}
                  </span>
                )}
                {hasData && (
                  <span className="flex sm:hidden flex-wrap gap-1 mt-auto pb-0.5">
                    {s.rin > 0 && <span className="w-2 h-2 rounded-full bg-[#0D9488]" />}
                    {s.rout > 0 && <span className="w-2 h-2 rounded-full bg-[#DC2626]" />}
                    {(s.pin > 0 || s.pout > 0) && <span className={`w-2 h-2 rounded-full border ${s.overdue ? "border-[#DC2626] bg-[#DC2626]/50 animate-pulse" : "border-dashed border-[#D97706] bg-[#D97706]/30"}`} />}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen(null)}>
        <DialogContent data-testid="calendar-day-dialog" className="max-w-lg rounded-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#0F172A] flex items-center justify-between gap-3 pr-6">
              <span>{dayOpen ? fmtDate(dayOpen) : ""} — transakcije</span>
              <Button
                data-testid="calendar-add-plan-btn"
                size="sm"
                variant="outline"
                className="rounded-sm border-[#0D9488]/40 text-[#0D9488] hover:bg-[#0D9488]/5 h-8 shrink-0"
                onClick={() => { setPlanDate(dayOpen); setDayOpen(null); }}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Novi plan
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {openList.map((t) => (
              <div key={t.id} className={`flex items-center justify-between gap-3 border rounded-sm px-3 py-2 ${t.status === "planirano" ? "border-dashed border-[#D97706]/60" : "border-[#E2E8F0]"}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{t.counterparty || t.category}</p>
                  <p className="text-xs text-[#475569] truncate" title={t.note || ""}>{t.category}{t.status === "planirano" ? " · planirano" : ""}{t.note ? ` · ${t.note}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.status === "planirano" && (
                    <Badge className="rounded-sm text-[10px] bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1">
                      <CalendarClock className="w-3 h-3" /> {t.date < today ? "dospjelo" : "plan"}
                    </Badge>
                  )}
                  <p className={`tabular-nums font-semibold text-sm ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                    {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                  </p>
                </div>
              </div>
            ))}
            {dayOpen && <ProjectionCard fixedDate={dayOpen} compact />}
          </div>
        </DialogContent>
      </Dialog>

      <TransactionDialog
        open={!!planDate}
        onOpenChange={(o) => !o && setPlanDate(null)}
        banks={banks}
        categories={categories}
        editing={null}
        defaults={planDate ? { date: planDate, status: "planirano" } : undefined}
        onSaved={load}
      />
    </div>
  );
}
